import React, { useState } from 'react';
import { FileCode, RefreshCw, Send, Archive } from 'lucide-react';
import api from '../../services/api';
import { useAuthStore } from '../../store/useStore';
import { notificaErrore, notificaConferma, notificaAvviso, messaggioDa } from '../../utils/notifiche';
import { dataOraIta } from '../../utils/oraItaliana';
import '../../styles/StatoSdi.css';

/**
 * StatoSdi — lo stato della fattura elettronica di un documento, e i gesti
 * di questo blocco (blocco 6, migration 258; Aruba, proposta 19):
 *   - «Scarica XML»: il file da caricare sul portale Fatture e
 *     Corrispettivi (provider «manuale»);
 *   - «Rigenera l'XML»: dopo aver corretto quello che il motivo dice;
 *   - «Invia allo SdI»: solo col provider «aruba», per un XML pronto o dopo
 *     un errore di Aruba (lo stesso file, mai uno nuovo);
 *   - «Conservata il …» e «Scarica il pacchetto» (solo amministrazione):
 *     il pacchetto di conservazione che tiene Aruba;
 *   - le notifiche dello SdI lette da Aruba (migration 336): il tipo, la data e, per lo scarto, i codici con la
 *     descrizione (senza i valori: li toglie il server). E il motivo dello
 *     scarto quando la notifica non e' ancora arrivata.
 * E dopo uno scarto dello SdI (domanda 30, Alex 15/10/2026) lo scarto resta
 * visibile finche' il documento corretto non e' ripartito: «corretta, da
 * trasmettere», e «Segna come trasmessa» quando la fattura e' stata
 * caricata sul portale (solo col provider «manuale»), con chi e quando. Lo
 * stato lo ricava il server (fe_dopo_scarto, dalla funzione del database
 * fe_dopo_scarto() della migration 335), come il resto.
 * Chi decide se il documento va allo SdI, se l'XML e' valido e cosa si puo'
 * spedire e' il server: qui si mostra e basta.
 */
export const STATI_SDI = {
    non_dovuta:          { testo: 'non va allo SdI', classe: 'nulla' },
    da_correggere:       { testo: 'XML da correggere', classe: 'errore' },
    pronta:              { testo: 'XML pronto', classe: 'pronta' },
    /* Aruba ha il file e non l'ha ancora passato allo SdI: mai «inviata»
       prima del tempo (Aruba ha 24 ore per ogni passo). */
    presa_in_carico:     { testo: 'presa in carico da Aruba', classe: 'attesa' },
    errore_elaborazione: { testo: 'Aruba non l\'ha elaborata', classe: 'errore' },
    inviata:             { testo: 'inviata allo SdI', classe: 'attesa' },
    consegnata:          { testo: 'consegnata', classe: 'ok' },
    mancata_consegna:    { testo: 'mancata consegna', classe: 'attesa' },
    recapito_impossibile: { testo: 'recapito impossibile', classe: 'attesa' },
    scartata:            { testo: 'scartata dallo SdI', classe: 'errore' },
};
const SCARICABILE = ['pronta', 'presa_in_carico', 'errore_elaborazione', 'inviata', 'consegnata',
                     'mancata_consegna', 'recapito_impossibile', 'scartata'];
/* Lo scarto non ancora chiuso (fe_dopo_scarto): prende il posto dello stato
   della colonna, che direbbe «XML pronto» o «inviata» come se lo scarto non
   ci fosse stato. */
export const STATI_DOPO_SCARTO = {
    da_trasmettere: { testo: 'corretta, da trasmettere', classe: 'attesa' },
    attende_esito:  { testo: 'corretta, inviata: attende lo SdI', classe: 'attesa' },
};
/* Gli stati in cui l'XML si rifa' e serve rifarlo: quelli di genera() nel
   server, tolto «pronta» (un XML gia' buono non va rifatto). Lo leggono il
   pulsante qui sotto e «Correggi e reinvia» delle Fatture: una lista sola. */
export const DA_RIGENERARE = ['da_correggere', 'scartata'];
const SPEDIBILE = ['pronta', 'errore_elaborazione'];
/* I tipi delle notifiche dello SdI, come li legge chi lavora. */
const NOTIFICHE_SDI = {
    RC: 'Ricevuta di consegna',
    NS: 'Notifica di scarto',
    MC: 'Mancata consegna',
    NE: 'Esito del committente',
    DT: 'Decorrenza dei termini',
    AT: 'Recapito impossibile',
};
const ESITI_COMMITTENTE = { EC01: 'accettata', EC02: 'rifiutata' };
const giornoOra = (v) => (v ? new Date(v).toLocaleString('it-IT', {
    timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

/** Un file dal server, salvato col nome che dice il server. La risposta e'
 *  un blob anche quando e' un errore: il motivo sta dentro, in JSON. */
async function scaricaFile(indirizzo, nomeRipiego, ripiego) {
    try {
        const r = await api.get(indirizzo, { responseType: 'blob' });
        const nome = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '')?.[1] || nomeRipiego;
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = nome;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
        let msg = ripiego;
        try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch (x) { /* resta il ripiego */ }
        notificaErrore(msg);
    }
}

/**
 * Rifa' l'XML con la rotta di sempre (POST /fatturazione/:id/xml) e dice
 * com'e' andata: «pronta» e' una conferma, il resto un avviso, un rifiuto
 * un errore col motivo del server. Restituisce true se la rotta ha
 * risposto. Una funzione sola per il pulsante di questo componente e per
 * «Correggi e reinvia» della schermata Fatture (stile «Console»).
 */
export async function rigeneraXml(fattura) {
    try {
        const r = await api.post(`/fatturazione/${fattura.id}/xml`);
        if (r.data?.data?.stato === 'pronta') notificaConferma(r.data.message);
        else notificaAvviso(r.data?.message || 'La fattura elettronica è ancora da correggere.');
        return true;
    } catch (e) {
        notificaErrore(messaggioDa(e, 'Non riesco a rigenerare la fattura elettronica'));
        return false;
    }
}

/**
 * «Segna come trasmessa» (domanda 30, migration 335): dopo la conferma,
 * la rotta di sempre (POST /fatturazione/:id/xml/trasmessa). Restituisce
 * true se la fattura e' stata segnata. Una funzione sola per il pulsante di
 * questo componente e per la riga aperta dello scarto delle Fatture (stile
 * «Console», che il gesto lo tiene li' e non nella cella).
 */
export async function segnaComeTrasmessa(fattura) {
    if (!window.confirm('Hai caricato la fattura corretta sul portale Fatture e Corrispettivi?\n'
        + 'Segnata come trasmessa, esce dalle fatture da correggere.')) return false;
    try {
        const r = await api.post(`/fatturazione/${fattura.id}/xml/trasmessa`);
        notificaConferma(r.data?.message || 'Fattura segnata come trasmessa.');
        return true;
    } catch (e) {
        notificaErrore(messaggioDa(e, 'Non riesco a segnare la fattura come trasmessa'));
        return false;
    }
}

/**
 * L'XML di un documento come lo da' la rotta (GET /fatturazione/:id/xml):
 * il file e il nome col quale il portale lo vuole. Un rifiuto solleva un
 * errore col motivo del server, che sta dentro il blob, in JSON. Lo usano
 * «Scarica XML» qui sotto e «Vedi XML» della schermata Fatture.
 */
export async function leggiXml(fattura, ripiego = 'Non riesco a leggere la fattura elettronica') {
    try {
        const r = await api.get(`/fatturazione/${fattura.id}/xml`, { responseType: 'blob' });
        const nome = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '')?.[1] || `fattura_${fattura.id}.xml`;
        return { blob: r.data, nome };
    } catch (e) {
        let msg = ripiego;
        try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch (x) { /* resta il ripiego */ }
        throw new Error(msg);
    }
}

/* `rigenera` e `segna`: false dove il gesto sta altrove, accanto al motivo
   (la riga aperta dello scarto nelle Fatture): due pulsanti per lo stesso
   gesto nella stessa riga sono uno di troppo. */
const StatoSdi = ({ fattura, onCambiata, compatto = false, rigenera: conRigenera = true, segna: conSegna = true }) => {
    const [inCorso, setInCorso] = useState(false);
    const amministra = useAuthStore((s) => s.user)?.ruolo === 'admin';
    const dopoScarto = STATI_DOPO_SCARTO[fattura.fe_dopo_scarto];
    const segnata = fattura.fe_stato === 'inviata' && fattura.fe_segnata_il
        ? { testo: 'segnata come trasmessa', classe: 'attesa' } : null;
    const st = dopoScarto || segnata || STATI_SDI[fattura.fe_stato];
    if (!st) return compatto ? <span className="sdi-nessuno">—</span> : null;
    const chiQuando = segnata
        ? `il ${dataOraIta(fattura.fe_segnata_il)}${fattura.fe_segnata_da ? ` da ${fattura.fe_segnata_da}` : ''}`
        : null;

    const scarica = () => scaricaFile(`/fatturazione/${fattura.id}/xml`, `fattura_${fattura.id}.xml`,
        'Non riesco a scaricare la fattura elettronica');
    const scaricaPacchetto = () => scaricaFile(`/fatturazione/${fattura.id}/conservazione`, `conservazione_${fattura.id}.zip`,
        'Non riesco a scaricare il pacchetto di conservazione');

    const rigenera = async () => {
        setInCorso(true);
        try {
            if (await rigeneraXml(fattura) && onCambiata) onCambiata();
        } finally {
            setInCorso(false);
        }
    };

    const invia = async () => {
        if (!window.confirm('Inviare la fattura elettronica allo SdI tramite Aruba?\n\n'
            + 'Non si torna indietro: una fattura consegnata si corregge solo con una nota di credito.')) return;
        setInCorso(true);
        try {
            const r = await api.post(`/fatturazione/${fattura.id}/invia-sdi`);
            notificaConferma(r.data?.message || 'Aruba ha preso in carico la fattura elettronica.');
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a inviare la fattura elettronica'));
        } finally {
            setInCorso(false);
            if (onCambiata) onCambiata();
        }
    };

    const notifiche = Array.isArray(fattura.fe_notifiche) ? fattura.fe_notifiche : [];
    const conScarto = notifiche.some((n) => n.tipo === 'NS');
    const conservata = fattura.fe_conservata_il
        ? new Date(fattura.fe_conservata_il).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' })
        : null;

    const segnaTrasmessa = async () => {
        setInCorso(true);
        try {
            if (await segnaComeTrasmessa(fattura) && onCambiata) onCambiata();
        } finally {
            setInCorso(false);
        }
    };

    return (
        <span className={`sdi ${compatto ? 'sdi--compatto' : ''}`}>
            <span className={`sdi-stato sdi-${st.classe}`}
                  title={['da_correggere', 'errore_elaborazione', 'pronta'].includes(fattura.fe_stato)
                      ? (fattura.fe_motivo || '')
                      : chiQuando ? `Segnata come trasmessa ${chiQuando}` : undefined}>
                {st.testo}
            </span>
            {!compatto && fattura.fe_invio === 'aruba' && SPEDIBILE.includes(fattura.fe_stato) && (
                <button type="button" className="sdi-btn" onClick={invia} disabled={inCorso}
                        title="Spedisce l'XML allo SdI tramite Aruba: prima la prova a vuoto, poi l'invio">
                    <Send size={14} aria-hidden="true" /> Invia allo SdI
                </button>
            )}
            {SCARICABILE.includes(fattura.fe_stato) && (
                <button type="button" className="sdi-btn" onClick={scarica} title="Scarica l'XML da caricare sul portale">
                    <FileCode size={14} aria-hidden="true" />{!compatto && ' Scarica XML'}
                </button>
            )}
            {conRigenera && DA_RIGENERARE.includes(fattura.fe_stato) && (
                <button type="button" className="sdi-btn" onClick={rigenera} disabled={inCorso}
                        title="Rifà l'XML dopo aver corretto quello che il motivo dice">
                    <RefreshCw size={14} aria-hidden="true" />{!compatto && ' Rigenera l\'XML'}
                </button>
            )}
            {!compatto && conservata && (
                <span className="sdi-conservata">Conservata il {conservata}</span>
            )}
            {!compatto && conservata && amministra && (
                <button type="button" className="sdi-btn" onClick={scaricaPacchetto}
                        title="Il pacchetto di conservazione che tiene Aruba: la fattura e le sue notifiche">
                    <Archive size={14} aria-hidden="true" /> Scarica il pacchetto
                </button>
            )}
            {conSegna && fattura.fe_dopo_scarto === 'da_trasmettere' && fattura.fe_invio !== 'aruba' && (
                <button type="button" className="sdi-btn" onClick={segnaTrasmessa} disabled={inCorso}
                        title="Dopo averla caricata sul portale: la fattura esce da quelle da correggere">
                    <Send size={14} aria-hidden="true" />{!compatto && ' Segna come trasmessa'}
                </button>
            )}
            {!compatto && chiQuando && (
                <span className="sdi-chi">{chiQuando}</span>
            )}
            {!compatto && (['da_correggere', 'pronta', 'errore_elaborazione'].includes(fattura.fe_stato)
                           || (fattura.fe_stato === 'scartata' && !conScarto)) && fattura.fe_motivo && (
                <span className="sdi-motivo">{fattura.fe_motivo}</span>
            )}
            {!compatto && notifiche.length > 0 && (
                <ul className="sdi-notifiche" aria-label="Notifiche dello SdI">
                    {notifiche.map((n, i) => (
                        <li key={`${n.tipo}-${i}`}>
                            <span className="sdi-notifica-tipo">{NOTIFICHE_SDI[n.tipo] || n.tipo}</span>
                            {n.esito_committente && ` — ${ESITI_COMMITTENTE[n.esito_committente] || n.esito_committente}`}
                            {' '}<span className="sdi-notifica-data">{giornoOra(n.consegnata_il || n.ricevuta_sdi_il || n.letta_il)}</span>
                            {Array.isArray(n.errori) && n.errori.length > 0 && (
                                <ul className="sdi-notifica-errori">
                                    {n.errori.map((e, j) => <li key={`${e.codice}-${j}`}>{e.codice} {e.descrizione}</li>)}
                                </ul>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </span>
    );
};

export default StatoSdi;
